/**
 * What the new-session picker may offer (RFC 0290 §3d, S5).
 *
 * `AgentPanel.handleCreate` used to write the window's project, deliberately,
 * because the inbox is a view across registries rather than one you can write
 * to. Making that a free choice is only safe now that a project host exists per
 * mounted project (S3): before it, choosing a project nothing serves produced a
 * session that was created, listed, and permanently silent, indistinguishable
 * in the UI from one whose worker is busy.
 *
 * So the picker offers every project in the World and SHOWS the ones nothing
 * mounts as unavailable, with the reason attached. Hiding them would leave a
 * person wondering where their project went; accepting them would leave them
 * waiting for a turn that is never coming. The reason names something they can
 * fix, which is to point a machine at the directory.
 *
 * Pure, so the gate is testable without a repo, a World or a browser.
 */

import type { ProjectAvailability } from '@mod/core/model'

export interface ProjectChoice {
  projectId: string
  name: string
  availability: ProjectAvailability
  /** True only for `unavailable`. `unknown` is offered: see below. */
  disabled: boolean
  /** Why it cannot be chosen, or which machines can answer it. Always present,
   *  because a greyed row with no explanation is worse than no row. */
  reason: string
  /** The devices that mount it, newest-seen first. Empty unless available. */
  hosts: string[]
}

/** What a project is worth showing about the machines that serve it. */
export interface ProjectHosting {
  availability: ProjectAvailability
  /** Display names of the present devices that mount it. */
  hosts: string[]
}

/**
 * Build the picker's rows.
 *
 * ONLY `unavailable` IS DISABLED. `unknown` means no present device has said
 * what it serves, which is what an older daemon looks like, and refusing on
 * "cannot tell" would grey out every project in the World and leave a person
 * unable to start any work. The asymmetry is on purpose: wrongly offering a
 * project costs one session that waits, wrongly refusing every project costs
 * the app.
 */
export function projectChoices(
  projects: readonly { projectId: string; displayName: string }[],
  hostingOf: (projectId: string) => ProjectHosting,
): ProjectChoice[] {
  return projects.map((project) => {
    const { availability, hosts } = hostingOf(project.projectId)
    return {
      projectId: project.projectId,
      name: project.displayName,
      availability,
      disabled: availability === 'unavailable',
      reason: reasonFor(availability, hosts),
      hosts,
    }
  })
}

function reasonFor(
  availability: ProjectAvailability,
  hosts: readonly string[],
): string {
  if (availability === 'unavailable') {
    // Names the fix, not the symptom. "Unavailable" tells a person nothing they
    // can act on; "no device here has this project" tells them to mount it.
    return 'No device here has this project. Open it on a machine running Mod to work in it.'
  }
  if (availability === 'unknown') {
    return 'No device has said which projects it serves yet.'
  }
  return hosts.length === 1
    ? `Served by ${hosts[0]}`
    : `Served by ${hosts.length} devices: ${hosts.join(', ')}`
}

/**
 * The project a create should actually land in, or null to refuse.
 *
 * A SECOND GATE, behind the disabled control. The button being disabled is a
 * fact about one render of one surface; this is the rule. A keyboard path, a
 * stale list, or a device that went quiet between the render and the click all
 * reach here, and `touchSession` fills in a missing registry entry rather than
 * skipping, so an accepted write against an unserved project does not fail
 * loudly. It mints a plausible row and strands the session.
 */
export function resolveCreateTarget(
  requested: string | undefined,
  fallback: string,
  choices: readonly ProjectChoice[],
): string | null {
  const target = requested ?? fallback
  const choice = choices.find((c) => c.projectId === target)
  // A project the picker never listed is the window's own, or a World that has
  // not synced. Neither is a reason to refuse: this gate exists to stop a
  // KNOWN-unservable choice, not to require the World to have arrived.
  if (!choice) return target
  return choice.disabled ? null : target
}
